import React, { useState } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import OrderItemsDetails, { OrderLineage } from './shared/OrderItemsDetails'
import SectionAccordion from './shared/SectionAccordion'
import Timeline, { FullTimeline, TimelineRow } from './shared/Timeline'
import DisposalNotice from './shared/DisposalNotice'
import SmsSection from './shared/sections/SmsSection'
import CommandLogsSection from './shared/sections/CommandLogsSection'
import DosingSection from './shared/sections/DosingSection'
import SentryAlertsSection from './shared/sections/SentryAlertsSection'
import IssueMealVoucherModal, { IssuedVoucher } from './shared/IssueMealVoucherModal'
import { LocalizationContext, LocalizationContextType, DEFAULT_LOCALIZATION_CONTEXT } from '../shared/contexts'
import { t } from '../../i18n'

interface QueuedOrderShowOrder {
  queued_order: {
    id: number
    state: string
    recipe_name: string | null
    marked_for_disposal: boolean
    disposal_reason: string | null
    disposed_at: string | null
    manual_disposal_requestable: boolean
    order: { id: number; number: string | null; customer_phone: string | null } | null
    timeline: FullTimeline
    measured_meal_weight_display: string | null
    product_lineage: OrderLineage
    heat_energy_in_pan_display: string | null
    main_batch_snapshot_ids: number[]
    voucher_issuable: boolean
    voucher: IssuedVoucher | null
  }
  localization_context?: LocalizationContextType
  default_voucher_validity_hours?: number | null
  can_view_order?: boolean
  can_view_snapshots?: boolean
  can_view_replenishment_orders?: boolean
}

const QueuedOrderShow = ({
  queued_order,
  localization_context,
  default_voucher_validity_hours,
  can_view_order,
  can_view_snapshots,
  can_view_replenishment_orders
}: QueuedOrderShowOrder) => {
  const basePath = `/queued_orders/${queued_order.id}`
  const parentOrder = queued_order.order
  const [issuedVoucher, setIssuedVoucher] = useState<IssuedVoucher | null>(null)
  const [issuingVoucher, setIssuingVoucher] = useState(false)
  // A voucher issued here supersedes whatever the meal had, and rules out issuing another.
  const voucher = issuedVoucher || queued_order.voucher
  const voucherIssuable = queued_order.voucher_issuable && issuedVoucher === null

  return (
    <LocalizationContext.Provider value={localization_context || DEFAULT_LOCALIZATION_CONTEXT}>
      {issuingVoucher &&
        <IssueMealVoucherModal
          queuedOrderId={queued_order.id}
          mealName={queued_order.recipe_name}
          orderId={parentOrder?.id || null}
          orderNumber={parentOrder?.number || null}
          customerPhone={parentOrder?.customer_phone || null}
          defaultValidityHours={default_voucher_validity_hours ?? null}
          onClose={() => setIssuingVoucher(false)}
          onIssued={(issued) => {
            setIssuedVoucher(issued)
            setIssuingVoucher(false)
          }}
        />
      }
      <Box className='order-show'>
        <Box className='order-show-header'>
          <div className='order-show-header-top'>
            <Typography variant='h4'>
              Order Item #{queued_order.id} - {queued_order.recipe_name || 'Unknown recipe'}
              <Chip className='ms-4' label={queued_order.state.replaceAll('_', ' ')} size='small' />
            </Typography>
            <div className='order-show-header-actions'>
              {parentOrder && can_view_order &&
                <a href={`/orders/${parentOrder.id}`} className='btn btn-link'>
                  ← Order #{parentOrder.number || parentOrder.id}
                </a>
              }
              {voucherIssuable &&
                <button type='button' className='btn btn-sm bw-button ms-2' onClick={() => setIssuingVoucher(true)}>
                  Issue voucher
                </button>
              }
              {queued_order.manual_disposal_requestable &&
                <a
                  href={`${basePath}/mark_for_disposal`}
                  data-method='put'
                  data-confirm={t('confirm_mark_for_disposal', { scope: 'orders' })}
                  className='btn btn-link text-danger'
                >
                  {t('mark_for_disposal', { scope: 'orders' })}
                </a>
              }
            </div>
          </div>

          <DisposalNotice
            markedForDisposal={queued_order.marked_for_disposal}
            disposalReason={queued_order.disposal_reason}
            disposedAt={queued_order.disposed_at}
            variant='banner'
          />

          {voucher &&
            <Typography variant='body2' className='mt-2'>
              Voucher <span className='voucher-token'>{voucher.token}</span>
              <Chip className='ms-2' label={voucher.status} size='small' />
            </Typography>
          }

          <Timeline timeline={queued_order.timeline}>
            <TimelineRow
              label={t('measured_meal_weight', { scope: 'orders' })}
              value={queued_order.measured_meal_weight_display}
              offsetSeconds={null}
            />
            <TimelineRow
              label={t('heat_energy_in_pan', { scope: 'orders' })}
              value={queued_order.heat_energy_in_pan_display}
              offsetSeconds={null}
            />
          </Timeline>
        </Box>

        <SectionAccordion title='Ingredients'>
          <OrderItemsDetails
            orderLineage={queued_order.product_lineage}
            canViewReplenishmentOrders={!!can_view_replenishment_orders}
          />
        </SectionAccordion>

        <SectionAccordion title='Command logs'>
          <CommandLogsSection basePath={basePath} />
        </SectionAccordion>

        <SectionAccordion title='Dosing logs'>
          <DosingSection basePath={basePath} />
        </SectionAccordion>

        <SectionAccordion title='SMS notifications'>
          <SmsSection basePath={basePath} />
        </SectionAccordion>

        <SectionAccordion title='Alerts'>
          <SentryAlertsSection basePath={basePath} />
        </SectionAccordion>

        <SectionAccordion title='Related snapshots'>
          {queued_order.main_batch_snapshot_ids.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>No related snapshots.</Typography>
          ) : (
            <table className='table table-robis'>
              <thead className='thead-robis'>
                <tr>
                  <th>Main batch snapshot ID</th>
                  <th className='snapshot-action-col'></th>
                </tr>
              </thead>
              <tbody>
                {queued_order.main_batch_snapshot_ids.map((snapshotId) => (
                  <tr key={snapshotId}>
                    <td>#{snapshotId}</td>
                    <td className='snapshot-action-col'>
                      {can_view_snapshots &&
                        <a
                          href={`/admin/main_batch_snapshots/${snapshotId}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='btn btn-sm bw-button'
                        >
                          See snapshot
                        </a>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionAccordion>
      </Box>
    </LocalizationContext.Provider>
  )
}

export default QueuedOrderShow
